import React from 'react';
import { DollarSign, BookOpen, ShieldCheck, Link2, PauseCircle } from 'lucide-react';

export const DIAG_ORDER = ['missing_revenue', 'qb_todo', 'warranty', 'data_issue', 'on_hold'];

export const DIAG_META = {
  missing_revenue: { label: 'Potential Missing Revenue', short: 'Missing Revenue', icon: DollarSign, chip: 'border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20' },
  qb_todo: { label: 'QuickBooks To-Do', short: 'QB To-Do', icon: BookOpen, chip: 'border-[#D4AF37]/60 text-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20' },
  warranty: { label: 'Warranty / HSP Claims', short: 'Warranty / HSP', icon: ShieldCheck, chip: 'border-sky-500/50 text-sky-300 bg-sky-500/10 hover:bg-sky-500/20' },
  data_issue: { label: 'Data / Linkage Issues', short: 'Data / Linkage', icon: Link2, chip: 'border-[#525252] text-[#D4D4D4] bg-[#262626] hover:bg-[#333]' },
  on_hold: { label: 'On Hold', short: 'On Hold', icon: PauseCircle, chip: 'border-[#3f3f3f] text-[#A3A3A3] bg-[#141414] hover:bg-[#1f1f1f]' },
};

export const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "$400.00" or "$400.00 + 3 with $ unknown" or "$ unknown (3)"
export const missingRevenueText = (mr) => {
  if (!mr || !mr.count) return '$0.00';
  const unk = mr.unknown_amount_count || 0;
  if (mr.known_amount > 0 && unk) return `${fmtMoney(mr.known_amount)} + ${unk} with $ unknown`;
  if (mr.known_amount > 0) return fmtMoney(mr.known_amount);
  return `$ unknown (${unk})`;
};

export const DiagBadge = ({ cat }) => {
  const m = DIAG_META[cat];
  if (!m) return null;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${m.chip}`}>{m.short}</span>;
};

export const BillingDiagnosticGroups = ({ groups, onSelect, testidPrefix = 'diag-group', active = null }) => (
  <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-chips`}>
    {DIAG_ORDER.map((key) => {
      const g = groups?.[key];
      if (!g) return null;
      const m = DIAG_META[key];
      const Icon = m.icon;
      return (
        <button
          key={key}
          type="button"
          data-testid={`${testidPrefix}-${key}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(key); }}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold transition-colors ${m.chip} ${active === key ? 'ring-2 ring-white/40' : ''} ${g.count === 0 ? 'opacity-50' : ''}`}
        >
          <Icon className="w-3.5 h-3.5" />
          {m.label}
          <span className="text-white" data-testid={`${testidPrefix}-${key}-count`}>{g.count}</span>
        </button>
      );
    })}
  </div>
);

export default BillingDiagnosticGroups;

// iter312 — Plain technician text from the canonical backend attribution (no extra lookup).
export const techNamesText = (r) => {
  const a = r?.tech_attribution;
  if (!a) return r?.tech_name || 'Unassigned';
  const leads = (a.technicians || []).filter((t) => t.role !== 'helper').map((t) => t.name);
  const helpers = (a.technicians || []).filter((t) => t.role === 'helper').map((t) => `${t.name} (helper)`);
  if (!leads.length && a.status === 'ambiguous') return ['Ambiguous', ...helpers].join(' + ');
  return [...(leads.length ? leads : ['Unassigned']), ...helpers].join(' + ');
};
export const techCount = (r) => (r?.tech_attribution?.technicians || []).length;
export const techLabel = (r) => `${techCount(r) > 1 ? 'Techs' : 'Tech'}: ${techNamesText(r)}`;
export const techTooltip = (r) => {
  const a = r?.tech_attribution;
  if (!a) return '';
  return `Source: ${a.source}${a.technician_id ? ` · id ${a.technician_id}` : ''}${a.detail ? ` · ${a.detail}` : ''}`;
};
